import { debounce } from 'throttle-debounce'

import { DEFAULT_STATE } from '../constants'
import { searchPageStore } from '../index'
import { mergeKeywordTerms, splitKeywordTerms } from '../keywordTerms'

// Typing commits to the store only after this pause: every committed keyword change triggers a
// full results fetch plus a history entry (SearchPage filter subscription -> fetchResults
// pushState), so a per-keystroke write would fire a search and stack a Back entry per prefix.
export const KEYWORD_COMMIT_DELAY = 400

/**
 * Sets the "search within results" keyword
 * @param {string} keyword - Free-text search string
 */
export const setKeyword = (keyword) => {
  searchPageStore.setState({ keyword })
}

/**
 * Debounced keyword commit used by the search-within-results input. A module singleton (not a
 * per-instance debouncer) so every path that clears the keyword can drop a pending commit,
 * including the ones the input itself cannot observe - see cancelKeywordCommit.
 */
export const commitKeyword = debounce(KEYWORD_COMMIT_DELAY, setKeyword)

/**
 * Drops a pending debounced commit. `upcomingOnly` leaves the debouncer reusable: a bare cancel()
 * latches its `cancelled` flag and would kill every later commit for the page's lifetime.
 */
export const cancelKeywordCommit = () => commitKeyword.cancel({ upcomingOnly: true })

/**
 * Clears the "search within results" keyword
 */
export const clearKeyword = () => {
  // Reset usually writes the same '' the store already holds, so the input's store subscription
  // does not rerender and the cancel it runs on external writes never fires - drop the pending
  // commit here, or 400ms later it would restore the keyword this call just cleared.
  cancelKeywordCommit()
  searchPageStore.setState(state => ({
    keyword: DEFAULT_STATE.keyword,
    keywordTerms: DEFAULT_STATE.keywordTerms,
    // Always changes, so the input resyncs its local draft and stops showing a term that the
    // clear just dropped.
    keywordClearCount: state.keywordClearCount + 1,
  }))
}

/**
 * Turns typed text into "search within results" chips and empties the draft
 * @param {string} text - Typed text; split into one chip per word or quoted phrase
 */
export const addKeywordTerms = (text) => {
  const newTerms = splitKeywordTerms(text)
  if (newTerms.length === 0) return

  cancelKeywordCommit()
  searchPageStore.setState(state => ({
    keyword: DEFAULT_STATE.keyword,
    keywordTerms: mergeKeywordTerms(state.keywordTerms, text),
    // The draft may never have been committed, in which case `keyword` is already '' and only the
    // counter tells the input to drop the text it shows.
    keywordClearCount: state.keywordClearCount + 1,
  }))
}

/**
 * Removes one "search within results" chip
 * @param {string} term - Chip to remove
 */
export const removeKeywordTerm = (term) => {
  searchPageStore.setState(state => ({
    keywordTerms: state.keywordTerms.filter(current => current !== term),
  }))
}

/**
 * Applies the chips and tags staged in the search-within panel below lg in one store write, so the
 * Search button triggers a single fetch
 * @param {{ terms: string[], tags: string[] }} staged - Chips and tags to apply
 */
export const applySearchWithin = ({ terms, tags }) => {
  cancelKeywordCommit()
  searchPageStore.setState(state => ({
    keyword: DEFAULT_STATE.keyword,
    keywordTerms: terms,
    tags,
    keywordClearCount: state.keywordClearCount + 1,
  }))
}
